import {ItemQuantity} from "./Inventory";

export type TechnologyDefinition = {
    id: string;
    name: string;
    description: string;
    cost: ItemQuantity[];
    prerequisites?: string[];
    iconId: string;
    reward?: string;
    chapter: string;
    section: string;
};

export type TechnologySection = {
    id: string;
    title: string;
    chapters: {
        id: string;
        title: string;
        technologies: TechnologyDefinition[];
    }[];
};

const basicTechnology: TechnologySection = {
    id: "basic-technology",
    title: "basic technology",
    chapters: [
        {
            id: "resource-acquisition",
            title: "resource acquisition",
            technologies: [
                {
                    id: "tech-wood-clearing",
                    name: "Wood Clearing",
                    description: "Clear trees to open up more coal and stone mining patches.",
                    cost: [{itemId: "sciencePack1", quantity: 10}],
                    prerequisites: [],
                    iconId: "tech-wood-clearing",
                    reward: "Unlock tree-clearing patch mini-game.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-steel-axes",
                    name: "Steel Axes",
                    description: "Sharper axes make each swing count twice for tree clearing.",
                    cost: [{itemId: "sciencePack1", quantity: 20}],
                    prerequisites: ["tech-wood-clearing", "tech-steel-smelting"],
                    iconId: "tech-steel-axes",
                    reward: "Tree chopping removes two hits per click.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-chainsaw",
                    name: "Chainsaw",
                    description: "Hold the mouse down to roar through trees while clearing patches.",
                    cost: [{itemId: "sciencePack1", quantity: 50}],
                    prerequisites: ["tech-steel-axes"],
                    iconId: "tech-chainsaw",
                    reward: "Hold-to-clear enabled for trees.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-boulder-clearing",
                    name: "Boulder Clearing",
                    description: "Smash stubborn boulders to free iron and copper patches.",
                    cost: [{itemId: "sciencePack1", quantity: 10}],
                    prerequisites: [],
                    iconId: "tech-boulder-clearing",
                    reward: "Unlock boulder-clearing patch mini-game.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-big-drills",
                    name: "Big Drills",
                    description: "Power drills automate boulder smashing while clearing patches.",
                    cost: [{itemId: "sciencePack1", quantity: 50}],
                    prerequisites: ["tech-boulder-clearing"],
                    iconId: "tech-big-drills",
                    reward: "Hold-to-clear enabled for boulders.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-iron-drill",
                    name: "Iron Drill",
                    description: "Reinforced drill tips crack boulders with half the effort.",
                    cost: [{itemId: "sciencePack1", quantity: 20}],
                    prerequisites: ["tech-boulder-clearing"],
                    iconId: "tech-iron-drill",
                    reward: "Boulder clearing requires -1 hit per crack click.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-electric-mining",
                    name: "Electric Mining",
                    description: "Harness electrical power to eliminate miner fuel requirements.",
                    cost: [{itemId: "sciencePack1", quantity: 30}],
                    prerequisites: ["tech-automation"],
                    iconId: "tech-electric-mining",
                    reward: "Electric Miner Drill recipe unlocked (faster, no coal).",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
                {
                    id: "tech-auto-miner-fueling",
                    name: "Automatic Miner Fueling",
                    description: "Miners automatically draw fuel from inventory when needed.",
                    cost: [{itemId: "sciencePack1", quantity: 20}],
                    prerequisites: [],
                    iconId: "tech-auto-miner-fueling",
                    reward: "Burner miners automatically consume fuel from inventory.",
                    chapter: "resource acquisition",
                    section: "basic technology",
                },
            ],
        },
        {
            id: "resource-processing",
            title: "resource processing",
            technologies: [
                {
                    id: "tech-steel-smelting",
                    name: "Steel Smelting",
                    description: "Unlock the ability to smelt steel plates.",
                    cost: [{itemId: "sciencePack1", quantity: 40}],
                    prerequisites: [],
                    iconId: "tech-steel-smelting",
                    reward: "Steel recipe unlocked in smelting.",
                    chapter: "resource processing",
                    section: "basic technology",
                },
                {
                    id: "tech-furnace-foundations",
                    name: "Unlock Furnace Foundations",
                    description: "Prepare furnace foundations through mini-games to expand smelting capacity.",
                    cost: [{itemId: "sciencePack1", quantity: 10}],
                    prerequisites: [],
                    iconId: "tech-furnace-foundations",
                    reward: "Unlock foundation-clearing mini-games for all smelter types.",
                    chapter: "resource processing",
                    section: "basic technology",
                },
                {
                    id: "tech-steel-furnace",
                    name: "Steel Furnace",
                    description: "Craft faster furnaces with bricks and steel plates.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 50},
                        {itemId: "sciencePack2", quantity: 50},
                    ],
                    prerequisites: ["tech-steel-smelting"],
                    iconId: "tech-steel-furnace",
                    reward: "Steel Furnace recipe unlocked (5 steel + 5 bricks, double speed).",
                    chapter: "resource processing",
                    section: "basic technology",
                },
                {
                    id: "tech-advanced-steel",
                    name: "Advanced Steel Production",
                    description: "Improved smelting techniques reduce steel's iron plate requirement.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 60},
                        {itemId: "sciencePack2", quantity: 30},
                    ],
                    prerequisites: ["tech-steel-furnace"],
                    iconId: "tech-advanced-steel",
                    reward: "Steel recipe improved to 4 iron plates → 1 steel (from 5:1).",
                    chapter: "resource processing",
                    section: "basic technology",
                },
                {
                    id: "tech-electric-furnace",
                    name: "Electric Furnace",
                    description: "Electrically-heated furnaces smelt without fuel consumption.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 80},
                        {itemId: "sciencePack2", quantity: 60},
                    ],
                    prerequisites: ["tech-steel-furnace"],
                    iconId: "tech-electric-furnace",
                    reward: "Electric Furnace recipe unlocked (1× speed, no fuel, 50kW power).",
                    chapter: "resource processing",
                    section: "basic technology",
                },
                {
                    id: "tech-auto-smelter-fueling",
                    name: "Automatic Smelter Fueling",
                    description: "Furnaces automatically draw fuel from inventory when needed.",
                    cost: [{itemId: "sciencePack1", quantity: 20}],
                    prerequisites: [],
                    iconId: "tech-auto-smelter-fueling",
                    reward: "Burner furnaces automatically consume fuel from inventory.",
                    chapter: "resource processing",
                    section: "basic technology",
                },
            ],
        },
        {
            id: "basic-automation",
            title: "basic automation",
            technologies: [
                {
                    id: "tech-automation",
                    name: "Automation",
                    description: "Understanding workflows unlocks assemblers.",
                    cost: [{itemId: "sciencePack1", quantity: 10}],
                    prerequisites: [],
                    iconId: "tech-automation",
                    reward: "Assembler recipe unlocked.",
                    chapter: "basic automation",
                    section: "basic technology",
                },
                {
                    id: "tech-transport-belt",
                    name: "Transport Belt",
                    description: "Move resources around your base automatically.",
                    cost: [{itemId: "sciencePack1", quantity: 10}],
                    prerequisites: ["tech-automation"],
                    iconId: "tech-transport-belt",
                    reward: "Transport belt recipe unlocked.",
                    chapter: "basic automation",
                    section: "basic technology",
                },
                {
                    id: "tech-inserter",
                    name: "Inserter",
                    description: "Pick up and drop off items to feed machines.",
                    cost: [{itemId: "sciencePack1", quantity: 10}],
                    prerequisites: ["tech-automation"],
                    iconId: "tech-inserter",
                    reward: "Inserter recipe unlocked.",
                    chapter: "basic automation",
                    section: "basic technology",
                },
                {
                    id: "tech-fast-assemblers",
                    name: "Fast Assemblers",
                    description: "Precision engineering accelerates assembly throughput.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 40},
                        {itemId: "sciencePack2", quantity: 40},
                    ],
                    prerequisites: ["tech-automation"],
                    iconId: "tech-fast-assemblers",
                    reward: "Fast Circuit Assembler recipe unlocked (1.5× speed, 2 module slots).",
                    chapter: "basic automation",
                    section: "basic technology",
                },
                {
                    id: "tech-logistics-placement",
                    name: "Logistics Placement",
                    description: "Learn to construct functional belt and inserter networks.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 50},
                        {itemId: "sciencePack2", quantity: 30},
                    ],
                    prerequisites: ["tech-transport-belt", "tech-inserter"],
                    iconId: "tech-logistics-placement",
                    reward: "Belts and inserters can now be placed and operated (future feature).",
                    chapter: "basic automation",
                    section: "basic technology",
                },
            ],
        },
    ],
};

const advancedTechnology: TechnologySection = {
    id: "advanced-technology",
    title: "advanced technology",
    chapters: [
        {
            id: "advanced-power",
            title: "advanced power",
            technologies: [
                {
                    id: "tech-solar-energy",
                    name: "Solar Energy",
                    description: "Capture sunlight to generate clean, renewable electricity.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 80},
                        {itemId: "sciencePack2", quantity: 60},
                    ],
                    prerequisites: [],
                    iconId: "tech-solar-energy",
                    reward: "Solar Panel recipe unlocked (40kW peak, no fuel).",
                    chapter: "advanced power",
                    section: "advanced technology",
                },
                {
                    id: "tech-energy-storage",
                    name: "Energy Storage",
                    description: "Battery arrays smooth power supply during darkness.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 100},
                        {itemId: "sciencePack2", quantity: 80},
                        {itemId: "sciencePack3", quantity: 40},
                    ],
                    prerequisites: ["tech-solar-energy"],
                    iconId: "tech-energy-storage",
                    reward: "Accumulator recipe unlocked (stores 100kW).",
                    chapter: "advanced power",
                    section: "advanced technology",
                },
                {
                    id: "tech-power-efficiency-1",
                    name: "Power Efficiency I",
                    description: "Optimized electrical systems reduce grid consumption.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 60},
                        {itemId: "sciencePack2", quantity: 50},
                    ],
                    prerequisites: [],
                    iconId: "tech-power-efficiency-1",
                    reward: "All electric miners and assemblers consume 10% less power.",
                    chapter: "advanced power",
                    section: "advanced technology",
                },
            ],
        },
        {
            id: "electronics-circuits",
            title: "electronics & circuits",
            technologies: [
                {
                    id: "tech-advanced-electronics",
                    name: "Advanced Electronics",
                    description: "Multi-layer circuit boards unlock complex machinery.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 100},
                        {itemId: "sciencePack2", quantity: 80},
                    ],
                    prerequisites: [],
                    iconId: "tech-advanced-electronics",
                    reward: "Advanced Circuit recipe unlocked.",
                    chapter: "electronics & circuits",
                    section: "advanced technology",
                },
                {
                    id: "tech-battery",
                    name: "Battery Technology",
                    description: "Electrochemical cells store energy for portable power.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 120},
                        {itemId: "sciencePack2", quantity: 90},
                    ],
                    prerequisites: ["tech-advanced-electronics"],
                    iconId: "tech-battery",
                    reward: "Battery recipe unlocked.",
                    chapter: "electronics & circuits",
                    section: "advanced technology",
                },
                {
                    id: "tech-processing-units",
                    name: "Processing Units",
                    description: "Ultra-dense circuitry for high-performance computing.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 150},
                        {itemId: "sciencePack2", quantity: 120},
                        {itemId: "sciencePack3", quantity: 80},
                    ],
                    prerequisites: ["tech-battery"],
                    iconId: "tech-processing-units",
                    reward: "Processing Unit recipe unlocked.",
                    chapter: "electronics & circuits",
                    section: "advanced technology",
                },
            ],
        },
        {
            id: "production-optimization",
            title: "production optimization",
            technologies: [
                {
                    id: "tech-speed-module-1",
                    name: "Speed Module I",
                    description: "Overclock accelerators boost machine throughput.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 100},
                        {itemId: "sciencePack2", quantity: 80},
                        {itemId: "sciencePack3", quantity: 50},
                    ],
                    prerequisites: ["tech-fast-assemblers", "tech-advanced-electronics"],
                    iconId: "tech-speed-module-1",
                    reward: "Speed Module 1 recipe unlocked (+20% speed, +50% energy).",
                    chapter: "production optimization",
                    section: "advanced technology",
                },
                {
                    id: "tech-productivity-module-1",
                    name: "Productivity Module I",
                    description: "Precision manufacturing grants bonus outputs.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 100},
                        {itemId: "sciencePack2", quantity: 80},
                        {itemId: "sciencePack3", quantity: 50},
                    ],
                    prerequisites: ["tech-fast-assemblers", "tech-advanced-electronics"],
                    iconId: "tech-productivity-module-1",
                    reward: "Productivity Module 1 recipe unlocked (+4% production, -5% speed, +40% energy).",
                    chapter: "production optimization",
                    section: "advanced technology",
                },
                {
                    id: "tech-efficiency-module-1",
                    name: "Efficiency Module I",
                    description: "Power-saving circuits reduce grid strain.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 100},
                        {itemId: "sciencePack2", quantity: 80},
                        {itemId: "sciencePack3", quantity: 50},
                    ],
                    prerequisites: ["tech-fast-assemblers", "tech-advanced-electronics"],
                    iconId: "tech-efficiency-module-1",
                    reward: "Efficiency Module 1 recipe unlocked (-30% energy, min 20%).",
                    chapter: "production optimization",
                    section: "advanced technology",
                },
                {
                    id: "tech-advanced-assembly",
                    name: "Advanced Assembly Automation",
                    description: "Multi-tier production chains handle complex recipes.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 120},
                        {itemId: "sciencePack2", quantity: 100},
                        {itemId: "sciencePack3", quantity: 60},
                    ],
                    prerequisites: ["tech-fast-assemblers"],
                    iconId: "tech-advanced-assembly",
                    reward: "Assembly buffer capacity increased to 3× (from 2×).",
                    chapter: "production optimization",
                    section: "advanced technology",
                },
            ],
        },
        {
            id: "environmental",
            title: "environmental",
            technologies: [
                {
                    id: "tech-pollution-monitoring",
                    name: "Pollution Monitoring",
                    description: "Sensors track environmental contamination levels.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 40},
                        {itemId: "sciencePack2", quantity: 30},
                    ],
                    prerequisites: [],
                    iconId: "tech-pollution-monitoring",
                    reward: "Detailed pollution breakdown by source (mining/smelting/power).",
                    chapter: "environmental",
                    section: "advanced technology",
                },
                {
                    id: "tech-pollution-filters",
                    name: "Pollution Filters",
                    description: "Scrubber technology reduces industrial emissions.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 80},
                        {itemId: "sciencePack2", quantity: 70},
                        {itemId: "sciencePack3", quantity: 50},
                    ],
                    prerequisites: ["tech-pollution-monitoring"],
                    iconId: "tech-pollution-filters",
                    reward: "All pollution generation reduced by 25%.",
                    chapter: "environmental",
                    section: "advanced technology",
                },
                {
                    id: "tech-advanced-pollution-control",
                    name: "Advanced Pollution Control",
                    description: "Next-generation scrubbers dramatically cut emissions.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 150},
                        {itemId: "sciencePack2", quantity: 120},
                        {itemId: "sciencePack3", quantity: 100},
                    ],
                    prerequisites: ["tech-pollution-filters"],
                    iconId: "tech-advanced-pollution-control",
                    reward: "All pollution generation reduced by additional 25% (50% total).",
                    chapter: "environmental",
                    section: "advanced technology",
                },
            ],
        },
        {
            id: "advanced-science",
            title: "advanced science",
            technologies: [
                {
                    id: "tech-science-pack-2",
                    name: "Intermediate Science Production",
                    description: "Understanding steel circuits unlocks Science Pack 2.",
                    cost: [{itemId: "sciencePack1", quantity: 30}],
                    prerequisites: ["tech-steel-smelting"],
                    iconId: "tech-science-pack-2",
                    reward: "Science Pack 2 recipe unlocked.",
                    chapter: "advanced science",
                    section: "advanced technology",
                },
                {
                    id: "tech-science-pack-3",
                    name: "Advanced Science Production",
                    description: "Complex multi-layer circuits enable Science Pack 3.",
                    cost: [
                        {itemId: "sciencePack1", quantity: 150},
                        {itemId: "sciencePack2", quantity: 120},
                    ],
                    prerequisites: ["tech-advanced-electronics", "tech-science-pack-2"],
                    iconId: "tech-science-pack-3",
                    reward: "Science Pack 3 recipe unlocked.",
                    chapter: "advanced science",
                    section: "advanced technology",
                },
            ],
        },
    ],
};

export const TECHNOLOGY_SECTIONS: TechnologySection[] = [basicTechnology, advancedTechnology];
export const TECHNOLOGIES: Record<string, TechnologyDefinition> = Object.fromEntries(
    TECHNOLOGY_SECTIONS.flatMap(section =>
        section.chapters.flatMap(chapter =>
            chapter.technologies.map(tech => [tech.id, tech]),
        ),
    ),
);

export const getTechnologyScienceWeight = (technology: TechnologyDefinition) => {
    const scienceCosts = technology.cost.filter(cost => cost.itemId.startsWith("sciencePack"));
    return scienceCosts.reduce((sum, cost) => sum + cost.quantity, 0);
};
